import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  TrendingUp,
  Users,
  Building2,
  DollarSign,
  PieChart,
  BarChart3,
  Download,
  FileSpreadsheet,
  ChevronDown,
  ChevronUp,
  Target,
  Briefcase,
  Server,
  Megaphone,
  Shield,
  Calculator,
  CheckCircle,
  AlertCircle,
  Home,
  KeyRound,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import FinancialProjectionsPDF from "@/components/FinancialProjectionsPDF";
import { PDFDownloadLink } from "@react-pdf/renderer";

// =============================================================================
// REAL-ESTATE / SBA CAPITAL OVERLAY — Plan C only (Ronan Rd HQ)
// -----------------------------------------------------------------------------
// Plans A + B have been retired. Only the Ronan Rd (Plan C) scenario is active.
// All figures synchronized with the SBA Excel workbook served from
// /api/investor/plan-c/proforma.xlsx.
// =============================================================================
const REAL_ESTATE_OVERLAY = {
  ronan: {
    id: "ronan",
    label: "Plan C · 16651 Ronan Rd HQ",
    short: "16651 Ronan Rd (Greenspoint)",
    address: "16651 Ronan Rd, Houston, TX 77060",
    sqft: "22,750 SF \u00b7 3 Stories \u00b7 Class-A Corporate Office on 5.08 Acres",
    accentColor: "amber",
    // Plan C capital stack: $3.456M SBA 504 (acquisition + reno + marketing) + $500K separate LOC = $4.49M total
    purchasePrice: 3_500_000,
    renovation: 160_000,
    equipment: 0,
    inventory: 0,
    workingCapital: 650_000,    // structured as separate revolving LOC
    marketing: 180_000,         // statewide B2B marketing launch
    totalCapital: 4_490_000,
    sbaLoan: 3_456_000,         // SBA 504 portion (acquisition + reno + marketing, ~90%)
    equityInjection: 384_000,
    monthlyDebtService: 32_650,
    annualDebtService: 391_800,
    // Owner-occupied revenue mix: Water/Cap Projects + Medical Devices +
    // Backorder Sourcing (6-yr benchmark, now AskIsaac-automated & scaling
    // regionally/statewide) + Healthcare Construction + Private-Label Gloves
    // (Intco) + Private-Label Microfiber (Texas MF) + BioBurden Cleaning.
    // Y1 total: $4.54M | Y2: $7.59M | Y3: $11.64M — matches Excel pro-forma exactly.
    // Backorder Sourcing is the flagship — Sterile Mate's 6-year door-opener
    // into every Texas hospital system, now automated for 3-5x volume growth.
    revenue: { year1: 4_541_860, year2: 7_585_776, year3: 11_641_336 },
    netIncome: { year1: 948_189, year2: 2_366_184, year3: 4_254_596 },
    rentalIncome: { year1: 0, year2: 0, year3: 0 },  // owner-occupied — no anchor tenant
    dscr: { year1: 3.33, year2: 6.71, year3: 11.22 },
    notes: [
      "Owner-occupied — Sterile Mate uses all three floors. No third-party tenants; the property functions as the company\u2019s single-tenant Class-A HQ.",
      "Floor 1 (10,438 SF): HQ, showroom, training center. Floor 2 (12,640 SF): 3,500 SF short-stock warehouse + Texas-metro sales office. Floor 3: executive suite + boardroom.",
      "Backorder Sourcing is the flagship revenue engine — Sterile Mate\u2019s 6-year benchmark and door-opener into every Texas hospital system. Now fully automated via the AskIsaac RFQ platform, it scales from $1.2M Y1 to $3.2M Y3 as we extend regionally and statewide. Supporting streams: Water Purification & Healthcare Capital Projects ($1.1M Y1), Medical Device Sales ($800K Y1), Healthcare Construction / GC ($400K Y1), Sterile Mate Private-Label Exam Gloves ($749K Y1 direct-import — 8 containers · 9,040 cartons · 3.5-mil + 4.0-mil nitrile at 200 pcs/box · scaling to $1.54M Y2 and $2.79M Y3), Private-Label Microfiber (Texas MF, $92.5K Y1), and Specialized BioBurden Cleaning ($200K Y1 · 5 hospitals · OR beds, stretchers, ICU beds, OR tables & poles).",
      "SBA 504 on $3.456M (acquisition $3.5M + reno $160K + marketing $180K financed at ~90%) — fixed-rate 25-year, $32,650/mo P&I.",
      "Separate $500K revolving LOC funds operating capital — preserves the SBA 504 facility for real-estate deployment only.",
      "5.08-acre site — 88 on-site parking spaces; 2.84-acre platted expansion pad for future Distribution / Medical Device Packaging Center; 2.23 acres of flex / fleet-parking land.",
      "Beltway 8 + I-45 intersection \u00b7 1.5 mi from ExxonMobil campus \u00b7 8 min to IAH.",
    ],
  },
};

const SCENARIO_DEFAULT = "ronan";

// Financial Data - 3 Year Projections (LEAN MODEL - Profitable from Year 1)
const FINANCIAL_DATA = {
  assumptions: {
    avgRevenuePerLocation: {
      year1: 1200, // Blended avg (hospitals + clinics)
      year2: 1100, // Volume discounts kick in
      year3: 1000  // Enterprise pricing
    },
    setupFeePerCustomer: {
      year1: 18000, // Avg of $15K-$25K
      year2: 30000, // Larger deals
      year3: 55000  // Enterprise implementations
    },
    customerChurn: 0.05, // 5% annual churn
    revenueGrowthFromExpansion: 0.10, // 10% expansion revenue from existing customers
  },
  
  // ============================================
  // YEAR 1 - LAUNCH PHASE (LEAN: 3-4 people)
  // ============================================
  year1: {
    phase: "Launch",
    customers: {
      healthSystems: 4,
      totalLocations: 45,
      breakdown: {
        hospitals: 5,
        clinics: 40
      }
    },
    revenue: {
      recurring: 648000,      // 45 locations × $1,200 avg × 12 months
      setupFees: 72000,       // 4 customers × $18,000 avg
      professionalServices: 25000,
      total: 745000
    },
    expenses: {
      personnel: {
        ceoFounder: 125000,   // CEO salary (capped)
        officeManagerEA: 55000, // Office Manager / Executive Assistant
        bookkeeper: 50000,    // Bookkeeper
        warehouseCoordinator: 46000, // Warehouse Logistics Coordinator
        salesAssistant: 40000, // Sales Assistant
        salesRepWater: 42500,  // Sales Rep — Water Purification (+ 15% commission)
        salesRepJanitorial: 42500, // Sales Rep — Janitorial Sales (+ 15% commission)
        salesRepMedical: 42500, // Sales Rep — Medical Device Sales (+ 15% commission)
        outsourcedDeveloper: 60000, // Full Stack Developer ($5K/mo contractor)
        benefits: 66525,      // ~15% of FTE salaries ($443,500)
        total: 570025
      },
      infrastructure: {
        cloudHosting: 24000,  // AWS/GCP - $2K/mo (lean start)
        database: 9600,       // MongoDB Atlas - $800/mo
        monitoring: 3600,     // Basic monitoring
        security: 9600,       // HIPAA compliance tools
        devTools: 6000,       // GitHub, CI/CD
        total: 52800
      },
      software: {
        crm: 3600,            // HubSpot free tier + starter
        support: 2400,        // Basic help desk
        communication: 2400,  // Slack, Zoom
        productivity: 1800,   // Google Workspace
        other: 1800,
        total: 12000
      },
      marketing: {
        digitalAds: 15000,    // Targeted LinkedIn/Google
        contentMarketing: 8000,
        tradeShows: 10000,    // 1-2 key conferences
        website: 4000,
        collateral: 3000,
        total: 40000
      },
      operations: {
        legal: 20000,         // Contracts, compliance
        accounting: 12000,    // Bookkeeping, tax prep
        insurance: 15000,     // E&O, Cyber, General
        bankFees: 2000,
        travel: 12000,        // Customer visits (lean)
        miscellaneous: 6000,
        total: 67000
      },
      totalExpenses: 683050
    },
    metrics: {
      grossMargin: 0.87,
      netIncome: 61950,
      profitMargin: 0.083,
      runway: "Break-even with investment runway",
      headcount: 4,
      ltv: 86400,
      cac: 22000,
      ltvCacRatio: 3.9
    }
  },

  // ============================================
  // YEAR 2 - GROWTH PHASE (LEAN: 8-9 people)
  // ============================================
  year2: {
    phase: "Growth",
    customers: {
      healthSystems: 14,      // +10 new customers
      totalLocations: 180,
      breakdown: {
        hospitals: 22,
        clinics: 158
      }
    },
    revenue: {
      recurring: 2376000,     // 180 locations × $1,100 avg × 12 months
      setupFees: 300000,      // 10 new customers × $30,000 avg
      professionalServices: 120000,
      expansionRevenue: 65000,
      total: 2861000
    },
    expenses: {
      personnel: {
        ceoFounder: 200000,   // CEO salary with raise
        vpCustomerSuccess: 130000, // Player/coach - manages + does work
        customerSuccessMgr: 72000, // 1 CSM (hire #2 in Q4)
        implementationSpec: 70000, // Also handles training
        supportLead: 68000,   // Handles Tier 1 & 2
        seniorDev1: 145000,   // Core platform
        seniorDev2: 140000,   // Features + integrations
        devOps: 125000,       // Infrastructure + security
        contractors: 40000,   // Overflow only
        benefits: 149000,     // ~15% of salaries
        payrollTaxes: 90000,  // ~9% of salaries
        total: 1229000
      },
      infrastructure: {
        cloudHosting: 60000,  // Scales with usage
        database: 24000,      // MongoDB Atlas growth
        monitoring: 12000,    // Better observability
        security: 24000,      // Enhanced HIPAA tools
        devTools: 12000,
        backupDR: 8000,
        total: 140000
      },
      software: {
        crm: 18000,           // HubSpot/Salesforce
        support: 12000,       // Zendesk
        communication: 7200,  // Slack, Zoom
        productivity: 5400,   // Google Workspace
        hrPayroll: 8000,      // Gusto/Rippling
        other: 5400,
        total: 56000
      },
      marketing: {
        digitalAds: 48000,    // Increased spend
        contentMarketing: 24000,
        tradeShows: 40000,    // 3-4 conferences
        website: 10000,
        collateral: 8000,
        prAgency: 30000,      // Part-time PR help
        total: 160000
      },
      operations: {
        legal: 35000,
        accounting: 24000,
        insurance: 28000,
        bankFees: 5000,
        travel: 40000,
        recruiting: 20000,    // Lean recruiting
        miscellaneous: 12000,
        total: 164000
      },
      totalExpenses: 1849000
    },
    metrics: {
      grossMargin: 0.86,
      netIncome: 1012000,
      profitMargin: 0.354,
      runway: "Profitable - Fund Year 3 growth",
      headcount: 9,
      ltv: 95040,
      cac: 32000,
      ltvCacRatio: 2.97
    }
  },

  // ============================================
  // YEAR 3 - SCALE PHASE (LEAN: 18-20 people)
  // ============================================
  year3: {
    phase: "Scale",
    customers: {
      healthSystems: 32,      // +18 new customers
      totalLocations: 520,
      breakdown: {
        hospitals: 58,
        clinics: 462
      }
    },
    revenue: {
      recurring: 6240000,     // 520 locations × $1,000 avg × 12 months
      setupFees: 990000,      // 18 new customers × $55,000 avg
      professionalServices: 350000,
      expansionRevenue: 280000,
      transactionFees: 150000,
      total: 8010000
    },
    expenses: {
      personnel: {
        ceoFounder: 225000,   // CEO with performance raise
        vpSales: 160000,      // Player/coach - sells too
        vpCustomerSuccess: 150000,
        salesTeam: 180000,    // 2 AEs (lean)
        customerSuccessTeam: 216000, // 3 CSMs
        implementationTeam: 140000, // 2 specialists
        supportTeam: 175000,  // Lead + 2 reps
        engineeringMgr: 155000, // Player/coach
        seniorDevs: 290000,   // 2 senior devs
        midDevs: 170000,      // 2 mid-level devs
        devOps: 135000,       // 1 senior DevOps
        productManager: 130000,
        contractors: 60000,
        benefits: 330000,
        payrollTaxes: 200000,
        total: 2716000
      },
      infrastructure: {
        cloudHosting: 144000, // Optimized scaling
        database: 48000,
        monitoring: 24000,
        security: 42000,
        devTools: 24000,
        backupDR: 18000,
        compliance: 24000,
        total: 324000
      },
      software: {
        crm: 42000,
        support: 30000,
        communication: 18000,
        productivity: 14000,
        hrPayroll: 24000,
        analytics: 18000,
        other: 10000,
        total: 156000
      },
      marketing: {
        digitalAds: 120000,
        contentMarketing: 60000,
        tradeShows: 100000,
        website: 20000,
        collateral: 15000,
        prAgency: 72000,
        brandAwareness: 40000,
        total: 427000
      },
      operations: {
        legal: 60000,
        accounting: 48000,
        insurance: 50000,
        bankFees: 15000,
        travel: 80000,
        recruiting: 50000,
        remoteStipends: 36000,
        miscellaneous: 25000,
        total: 364000
      },
      totalExpenses: 4127000
    },
    metrics: {
      grossMargin: 0.88,
      netIncome: 3883000,
      profitMargin: 0.485,
      runway: "Highly Profitable - Ready for expansion or exit",
      headcount: 19,
      ltv: 108000,
      cac: 32600,
      ltvCacRatio: 3.31
    }
  }
};

// Expandable Section Component
const ExpandableSection = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 bg-white hover:bg-slate-50 border border-slate-200 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Icon className="w-5 h-5 text-blue-900" />
          <span className="font-semibold text-slate-900">{title}</span>
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-slate-600" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-600" />
        )}
      </button>
      {isOpen && (
        <div className="p-4 bg-slate-50">
          {children}
        </div>
      )}
    </div>
  );
};

// Format currency
const formatCurrency = (amount) => {
  if (amount >= 1000000) {
    return `$${(amount / 1000000).toFixed(2)}M`;
  }
  if (amount >= 1000) {
    return `$${(amount / 1000).toFixed(0)}K`;
  }
  return `$${amount.toLocaleString()}`;
};

// Format percentage
const formatPercent = (value) => `${(value * 100).toFixed(1)}%`;

export default function FinancialProjectionsPage() {
  const [selectedYear, setSelectedYear] = useState('all');

  // ===== SBA Loan Scenario toggle (Plan A: Humble · Plan B: North Sam Houston) =====
  // Persisted in localStorage so it stays in sync with the InvestorPortalPage toggle.
  const [scenarioId, setScenarioId] = useState(() => {
    if (typeof window === "undefined") return SCENARIO_DEFAULT;
    return window.localStorage.getItem("sm_active_scenario") || SCENARIO_DEFAULT;
  });
  // Plans A + B retired. Only Plan C (Ronan Rd) remains.
  const allowedScenarios = useMemo(() => ["ronan"], []);
  const activeScenarioId = allowedScenarios.includes(scenarioId)
    ? scenarioId
    : allowedScenarios[0];
  const realEstate = REAL_ESTATE_OVERLAY[activeScenarioId] || REAL_ESTATE_OVERLAY[SCENARIO_DEFAULT];
  const switchScenario = (id) => {
    if (!allowedScenarios.includes(id)) return;
    setScenarioId(id);
    try {
      window.localStorage.setItem("sm_active_scenario", id);
    } catch { /* ignore */ }
  };

  // Scenario-adjusted year metrics (from canonical SBA workbook figures)
  const adjustedYears = useMemo(() => {
    const yearKeys = ["year1", "year2", "year3"];
    return yearKeys.map((k) => {
      const base = FINANCIAL_DATA[k];
      const rental = realEstate.rentalIncome[k];
      const adjustedRevenue = realEstate.revenue[k];          // total revenue from canonical Excel
      const adjustedNetIncome = realEstate.netIncome[k];      // profit-after-tax from canonical Excel
      const adjustedExpenses = adjustedRevenue - adjustedNetIncome;
      const adjustedMargin = adjustedNetIncome / adjustedRevenue;
      return {
        ...base,
        scenarioRental: rental,
        scenarioDebtService: realEstate.annualDebtService,
        scenarioDscr: realEstate.dscr[k],
        adjustedRevenue,
        adjustedExpenses,
        adjustedNetIncome,
        adjustedMargin,
      };
    });
  }, [realEstate]);

  const totalAdjustedNetIncome = adjustedYears.reduce((s, y) => s + y.adjustedNetIncome, 0);

  const years = adjustedYears;
  const isPlanB = activeScenarioId === "northsam";
  const isPlanC = activeScenarioId === "ronan";
  // Plan C is owner-occupied — no third-party rental income.
  // Plan B keeps the 2SIXTY3 multi-tenant offset.
  const hasRentalIncome = isPlanB;

  // Plan C — 7 diversified revenue streams (matches Excel proforma & business plan)
  const PLAN_C_STREAMS = [
    { name: "Water Purification & Healthcare Capital Projects", y1: 1_100_000, y2: 1_400_000, y3: 1_800_000, color: "cyan" },
    { name: "Medical Device Sales", y1: 800_000, y2: 1_100_000, y3: 1_500_000, color: "emerald" },
    { name: "Backorder Sourcing (AskIsaac-automated · 6-yr benchmark)", y1: 1_200_000, y2: 2_100_000, y3: 3_200_000, color: "amber" },
    { name: "Healthcare Construction (GC / Renovation)", y1: 400_000, y2: 900_000, y3: 1_500_000, color: "blue" },
    { name: "Consumables · Private-Label Exam Gloves (Intco 3.5-mil + 4.0-mil)", y1: 749_360, y2: 1_544_776, y3: 2_786_736, color: "emerald" },
    { name: "Consumables · Private-Label Microfiber (Texas MF, Blue/White/Black)", y1: 92_500, y2: 191_000, y3: 354_600, color: "blue" },
    { name: "Specialized BioBurden Cleaning (5 hospitals · OR beds / stretchers / ICU)", y1: 200_000, y2: 350_000, y3: 500_000, color: "cyan" },
  ];
  const pdfFileName = "SterileMate_Financial_Projections_PlanC_Ronan_2026.pdf";
  // Excel worksheet is served from the pro-forma endpoint we generate on the fly.
  const excelHref = `${process.env.REACT_APP_BACKEND_URL}/api/investor/plan-c/proforma.xlsx`;
  
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white/95 backdrop-blur-sm sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/investor" className="text-slate-600 hover:text-slate-900 transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-xl font-bold text-slate-900">3-Year Financial Projections</h1>
              <p className="text-sm text-slate-600">Sterile Mate LLC — Powered by AskIsaac</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href={excelHref}
              className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-slate-900 text-sm font-medium rounded-lg transition-colors"
              data-testid="download-sba-excel-btn"
            >
              <FileSpreadsheet className="w-4 h-4" />
              SBA Worksheet (Excel)
            </a>
            <PDFDownloadLink
              document={<FinancialProjectionsPDF scenario={activeScenarioId} />}
              fileName={pdfFileName}
              key={activeScenarioId}
              className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors"
              data-testid={`download-financial-pdf-${activeScenarioId}`}
            >
              {({ loading }) => (
                loading ? (
                  <>
                    <span className="animate-spin">...</span>
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Plan C PDF
                  </>
                )
              )}
            </PDFDownloadLink>
          </div>
        </div>
      </header>

      {/* Scenario banner — Plan C only (Plans A + B retired) */}
      <div className="border-b border-slate-200 bg-white backdrop-blur" data-testid="financials-scenario-banner">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-wrap items-center gap-3">
          <KeyRound className="w-4 h-4 text-slate-800" />
          <span className="text-[10px] font-extrabold tracking-[0.2em] uppercase text-slate-800">
            Active SBA Capital Plan
          </span>
          <p className="text-slate-900 text-sm sm:text-base font-semibold leading-tight" data-testid="scenario-active-label">
            {realEstate.label}
            <span className="text-slate-600 font-normal"> · {realEstate.short} · ${(realEstate.totalCapital / 1_000_000).toFixed(2)}M total ask</span>
          </p>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">

        {/* ====== REAL ESTATE / CAPITAL PLAN — scenario-aware ====== */}
        <section data-testid="capital-plan-section">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Building2 className={`w-5 h-5 ${isPlanC ? "text-slate-800" : isPlanB ? "text-emerald-700" : "text-blue-900"}`} />
            SBA Capital Plan &mdash; {realEstate.label}
          </h2>
          <div className={`rounded-xl border p-5 ${isPlanC ? "bg-amber-500/5 border-slate-200" : isPlanB ? "bg-emerald-500/5 border-slate-200" : "bg-slate-50 border-slate-200"}`}>
            <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
              <div className="min-w-0">
                <p className="text-slate-700 text-sm">
                  <span className="font-semibold text-slate-900">{realEstate.address}</span>
                </p>
                <p className="text-slate-600 text-xs mt-0.5">{realEstate.sqft}</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${isPlanC ? "bg-slate-100 text-slate-800" : isPlanB ? "bg-slate-100 text-emerald-700" : "bg-blue-100 text-blue-900"}`}>
                {isPlanC ? "Owner-occupied \u00b7 6 revenue streams" : isPlanB ? "Multi-tenant \u00b7 rental offset" : "Single-tenant \u00b7 Humble"}
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
              <div className="bg-white border border-slate-200 rounded-lg p-3 border border-slate-200">
                <p className="text-slate-600 text-xs">Total Capital Ask</p>
                <p className="text-xl font-bold text-slate-900">{formatCurrency(realEstate.totalCapital)}</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-3 border border-slate-200">
                <p className="text-slate-600 text-xs">{isPlanC ? "SBA 504 Loan" : "SBA 7(a) Loan"}</p>
                <p className="text-xl font-bold text-blue-900">{formatCurrency(realEstate.sbaLoan)}</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-3 border border-slate-200">
                <p className="text-slate-600 text-xs">Equity Injection</p>
                <p className="text-xl font-bold text-slate-800">{formatCurrency(realEstate.equityInjection)}</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-lg p-3 border border-slate-200">
                <p className="text-slate-600 text-xs">Monthly Debt Service</p>
                <p className="text-xl font-bold text-slate-900">{formatCurrency(realEstate.monthlyDebtService)}/mo</p>
              </div>
            </div>
            {/* DSCR + Rental Income strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                <p className="text-slate-600 text-xs">DSCR &mdash; Year 1</p>
                <p className={`text-xl font-bold ${realEstate.dscr.year1 >= 1.5 ? "text-emerald-700" : "text-slate-800"}`}>{realEstate.dscr.year1.toFixed(2)}x</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                <p className="text-slate-600 text-xs">DSCR &mdash; Year 2</p>
                <p className="text-xl font-bold text-emerald-700">{realEstate.dscr.year2.toFixed(2)}x</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                <p className="text-slate-600 text-xs">DSCR &mdash; Year 3</p>
                <p className="text-xl font-bold text-emerald-700">{realEstate.dscr.year3.toFixed(2)}x</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                <p className="text-slate-600 text-xs">3-Yr Rental Income</p>
                <p className="text-xl font-bold text-slate-900">
                  {formatCurrency(realEstate.rentalIncome.year1 + realEstate.rentalIncome.year2 + realEstate.rentalIncome.year3)}
                </p>
              </div>
            </div>
            <ul className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-2">
              {realEstate.notes.map((n, i) => (
                <li key={i} className="text-slate-700 text-sm flex items-start gap-2">
                  <CheckCircle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isPlanC ? "text-slate-800" : isPlanB ? "text-emerald-700" : "text-blue-900"}`} />
                  {n}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Executive Summary Cards */}
        <section>
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-900" />
            Executive Summary &mdash; {isPlanC ? "Plan C (owner-occupied \u00b7 6 revenue streams)" : isPlanB ? "Plan B (with rental offset)" : "Plan A"}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white border border-slate-200 rounded-xl p-4 border border-slate-200">
              <p className="text-slate-600 text-sm">Year 3 Revenue {hasRentalIncome && <span className="text-[10px] text-emerald-700">(incl. rental)</span>}</p>
              <p className="text-2xl font-bold text-emerald-700">{formatCurrency(adjustedYears[2].adjustedRevenue)}</p>
              <p className="text-xs text-slate-500">{Math.round((adjustedYears[2].adjustedRevenue / adjustedYears[0].adjustedRevenue - 1) * 100)}% growth from Y1</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4 border border-slate-200">
              <p className="text-slate-600 text-sm">Year 3 Net Income</p>
              <p className="text-2xl font-bold text-emerald-700">{formatCurrency(adjustedYears[2].adjustedNetIncome)}</p>
              <p className="text-xs text-slate-500">{(adjustedYears[2].adjustedMargin * 100).toFixed(0)}% profit margin</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4 border border-slate-200">
              <p className="text-slate-600 text-sm">Total Locations</p>
              <p className="text-2xl font-bold text-blue-900">520</p>
              <p className="text-xs text-slate-500">32 health systems</p>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4 border border-slate-200">
              <p className="text-slate-600 text-sm">3-Year Net Profit</p>
              <p className="text-2xl font-bold text-slate-900">{formatCurrency(totalAdjustedNetIncome)}</p>
              <p className="text-xs text-emerald-700">{totalAdjustedNetIncome > 0 ? "Profitable every year" : "Profitability ramps Y2"}</p>
            </div>
          </div>
        </section>

        {/* Year-over-Year Comparison */}
        <section>
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-900" />
            Year-over-Year Summary
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-slate-600 font-medium">Metric</th>
                  <th className="text-right py-3 px-4 text-slate-600 font-medium">Year 1<br/><span className="text-xs text-blue-900">Launch</span></th>
                  <th className="text-right py-3 px-4 text-slate-600 font-medium">Year 2<br/><span className="text-xs text-slate-800">Growth</span></th>
                  <th className="text-right py-3 px-4 text-slate-600 font-medium">Year 3<br/><span className="text-xs text-emerald-700">Scale</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                <tr className="hover:bg-white/30">
                  <td className="py-3 px-4 text-slate-900 font-medium">Health Systems</td>
                  <td className="py-3 px-4 text-right text-slate-700">4</td>
                  <td className="py-3 px-4 text-right text-slate-700">14</td>
                  <td className="py-3 px-4 text-right text-slate-700">32</td>
                </tr>
                <tr className="hover:bg-white/30">
                  <td className="py-3 px-4 text-slate-900 font-medium">Total Locations</td>
                  <td className="py-3 px-4 text-right text-slate-700">45</td>
                  <td className="py-3 px-4 text-right text-slate-700">180</td>
                  <td className="py-3 px-4 text-right text-slate-700">520</td>
                </tr>
                <tr className="hover:bg-white/30">
                  <td className="py-3 px-4 text-slate-900 font-medium">Total Revenue {hasRentalIncome && <span className="text-[10px] text-emerald-700 ml-1">(incl. rental)</span>}</td>
                  <td className="py-3 px-4 text-right text-emerald-700 font-semibold">{formatCurrency(adjustedYears[0].adjustedRevenue)}</td>
                  <td className="py-3 px-4 text-right text-emerald-700 font-semibold">{formatCurrency(adjustedYears[1].adjustedRevenue)}</td>
                  <td className="py-3 px-4 text-right text-emerald-700 font-semibold">{formatCurrency(adjustedYears[2].adjustedRevenue)}</td>
                </tr>
                {hasRentalIncome && (
                  <tr className="hover:bg-white/30 bg-emerald-500/5" data-testid="rental-income-row">
                    <td className="py-3 px-4 text-emerald-700 font-medium">+ {isPlanC ? "Ancillary Rental Income" : "2SIXTY3 Rental Income"}</td>
                    <td className="py-3 px-4 text-right text-emerald-700">{formatCurrency(adjustedYears[0].scenarioRental)}</td>
                    <td className="py-3 px-4 text-right text-emerald-700">{formatCurrency(adjustedYears[1].scenarioRental)}</td>
                    <td className="py-3 px-4 text-right text-emerald-700">{formatCurrency(adjustedYears[2].scenarioRental)}</td>
                  </tr>
                )}
                <tr className="hover:bg-white/30">
                  <td className="py-3 px-4 text-slate-900 font-medium">Total Expenses {hasRentalIncome ? "(incl. debt + property OPEX)" : "(incl. debt + property OPEX)"}</td>
                  <td className="py-3 px-4 text-right text-slate-700">{formatCurrency(adjustedYears[0].adjustedExpenses)}</td>
                  <td className="py-3 px-4 text-right text-slate-700">{formatCurrency(adjustedYears[1].adjustedExpenses)}</td>
                  <td className="py-3 px-4 text-right text-slate-700">{formatCurrency(adjustedYears[2].adjustedExpenses)}</td>
                </tr>
                <tr className="hover:bg-white/30 bg-amber-500/5" data-testid="debt-service-row">
                  <td className="py-3 px-4 text-slate-800 font-medium">SBA Debt Service (P&amp;I, included above)</td>
                  <td className="py-3 px-4 text-right text-slate-800">{formatCurrency(adjustedYears[0].scenarioDebtService)}</td>
                  <td className="py-3 px-4 text-right text-slate-800">{formatCurrency(adjustedYears[1].scenarioDebtService)}</td>
                  <td className="py-3 px-4 text-right text-slate-800">{formatCurrency(adjustedYears[2].scenarioDebtService)}</td>
                </tr>
                <tr className="hover:bg-white/30 bg-slate-100" data-testid="net-income-row">
                  <td className="py-3 px-4 text-slate-900 font-bold">Profit After Tax</td>
                  <td className="py-3 px-4 text-right text-emerald-700 font-bold">{formatCurrency(adjustedYears[0].adjustedNetIncome)}</td>
                  <td className="py-3 px-4 text-right text-emerald-700 font-bold">{formatCurrency(adjustedYears[1].adjustedNetIncome)}</td>
                  <td className="py-3 px-4 text-right text-emerald-700 font-bold">{formatCurrency(adjustedYears[2].adjustedNetIncome)}</td>
                </tr>
                <tr className="hover:bg-white/30">
                  <td className="py-3 px-4 text-slate-900 font-medium">Profit Margin</td>
                  <td className="py-3 px-4 text-right text-emerald-700">{(adjustedYears[0].adjustedMargin * 100).toFixed(0)}%</td>
                  <td className="py-3 px-4 text-right text-emerald-700">{(adjustedYears[1].adjustedMargin * 100).toFixed(0)}%</td>
                  <td className="py-3 px-4 text-right text-emerald-700">{(adjustedYears[2].adjustedMargin * 100).toFixed(0)}%</td>
                </tr>
                <tr className="hover:bg-white/30 bg-cyan-500/5" data-testid="dscr-row">
                  <td className="py-3 px-4 text-blue-900 font-medium">SBA DSCR</td>
                  <td className="py-3 px-4 text-right text-blue-900 font-bold">{adjustedYears[0].scenarioDscr.toFixed(2)}x</td>
                  <td className="py-3 px-4 text-right text-blue-900 font-bold">{adjustedYears[1].scenarioDscr.toFixed(2)}x</td>
                  <td className="py-3 px-4 text-right text-blue-900 font-bold">{adjustedYears[2].scenarioDscr.toFixed(2)}x</td>
                </tr>
                <tr className="hover:bg-white/30">
                  <td className="py-3 px-4 text-slate-900 font-medium">Headcount</td>
                  <td className="py-3 px-4 text-right text-slate-700">3</td>
                  <td className="py-3 px-4 text-right text-slate-700">9</td>
                  <td className="py-3 px-4 text-right text-slate-700">19</td>
                </tr>
                <tr className="hover:bg-white/30">
                  <td className="py-3 px-4 text-slate-900 font-medium">Gross Margin</td>
                  <td className="py-3 px-4 text-right text-slate-700">87%</td>
                  <td className="py-3 px-4 text-right text-slate-700">86%</td>
                  <td className="py-3 px-4 text-right text-slate-700">88%</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Detailed Year Breakdowns */}
        {years.map((year, idx) => (
          <section key={idx} className="space-y-4">
            <div className="flex items-center gap-3">
              <div className={`px-3 py-1 rounded-full text-sm font-semibold ${
                idx === 0 ? 'bg-slate-100 text-blue-900' :
                idx === 1 ? 'bg-slate-100 text-slate-800' :
                'bg-slate-100 text-emerald-700'
              }`}>
                Year {idx + 1}: {year.phase} Phase
              </div>
              <span className="text-slate-600 text-sm">
                {year.customers.healthSystems} Health Systems • {year.customers.totalLocations} Locations
              </span>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {/* Revenue Breakdown */}
              <ExpandableSection title={isPlanC ? "Revenue Breakdown \u00b7 Seven Streams" : "Revenue Breakdown"} icon={DollarSign} defaultOpen={idx === 0}>
                {isPlanC ? (
                  <div className="space-y-3" data-testid={`plan-c-revenue-streams-y${idx + 1}`}>
                    {PLAN_C_STREAMS.map((s) => {
                      const val = idx === 0 ? s.y1 : idx === 1 ? s.y2 : s.y3;
                      return (
                        <div key={s.name} className="flex justify-between items-center py-2 border-b border-slate-200 gap-3">
                          <span className="text-slate-700 text-sm">{s.name}</span>
                          <span className={`text-${s.color}-400 font-semibold whitespace-nowrap`}>{formatCurrency(val)}</span>
                        </div>
                      );
                    })}
                    <div className="flex justify-between items-center py-2 bg-slate-100 rounded px-2 -mx-2">
                      <span className="text-slate-900 font-bold">Total Revenue</span>
                      <span className="text-emerald-700 font-bold text-lg">
                        {formatCurrency(PLAN_C_STREAMS.reduce((s, r) => s + (idx === 0 ? r.y1 : idx === 1 ? r.y2 : r.y3), 0))}
                      </span>
                    </div>
                  </div>
                ) : (
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-slate-200">
                    <span className="text-slate-700">Recurring Subscription</span>
                    <span className="text-emerald-700 font-semibold">{formatCurrency(year.revenue.recurring)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-slate-200">
                    <span className="text-slate-700">Setup/Implementation Fees</span>
                    <span className="text-emerald-700 font-semibold">{formatCurrency(year.revenue.setupFees)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-slate-200">
                    <span className="text-slate-700">Professional Services</span>
                    <span className="text-emerald-700 font-semibold">{formatCurrency(year.revenue.professionalServices)}</span>
                  </div>
                  {year.revenue.expansionRevenue && (
                    <div className="flex justify-between items-center py-2 border-b border-slate-200">
                      <span className="text-slate-700">Expansion Revenue</span>
                      <span className="text-emerald-700 font-semibold">{formatCurrency(year.revenue.expansionRevenue)}</span>
                    </div>
                  )}
                  {year.revenue.transactionFees && (
                    <div className="flex justify-between items-center py-2 border-b border-slate-200">
                      <span className="text-slate-700">Transaction Fees</span>
                      <span className="text-emerald-700 font-semibold">{formatCurrency(year.revenue.transactionFees)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center py-2 bg-slate-100 rounded px-2 -mx-2">
                    <span className="text-slate-900 font-bold">Total Revenue</span>
                    <span className="text-emerald-700 font-bold text-lg">{formatCurrency(year.revenue.total)}</span>
                  </div>
                </div>
                )}
              </ExpandableSection>

              {/* Personnel Costs */}
              <ExpandableSection title="Personnel Costs" icon={Users} defaultOpen={idx === 0}>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {Object.entries(year.expenses.personnel).map(([key, value]) => {
                    if (key === 'total') return null;
                    const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
                    return (
                      <div key={key} className="flex justify-between items-center py-1">
                        <span className="text-slate-600 text-sm">{label}</span>
                        <span className="text-slate-700">{formatCurrency(value)}</span>
                      </div>
                    );
                  })}
                  <div className="flex justify-between items-center py-2 bg-red-50 rounded px-2 -mx-2 mt-2">
                    <span className="text-slate-900 font-bold">Total Personnel</span>
                    <span className="text-red-700 font-bold">{formatCurrency(year.expenses.personnel.total)}</span>
                  </div>
                </div>
              </ExpandableSection>

              {/* Infrastructure Costs */}
              <ExpandableSection title="Infrastructure & Technology" icon={Server}>
                <div className="space-y-2">
                  {Object.entries(year.expenses.infrastructure).map(([key, value]) => {
                    if (key === 'total') return null;
                    const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
                    return (
                      <div key={key} className="flex justify-between items-center py-1">
                        <span className="text-slate-600 text-sm">{label}</span>
                        <span className="text-slate-700">{formatCurrency(value)}</span>
                      </div>
                    );
                  })}
                  <div className="flex justify-between items-center py-2 bg-slate-100 rounded px-2 -mx-2 mt-2">
                    <span className="text-slate-900 font-semibold">Total Infrastructure</span>
                    <span className="text-slate-700 font-semibold">{formatCurrency(year.expenses.infrastructure.total)}</span>
                  </div>
                </div>
              </ExpandableSection>

              {/* Marketing Costs */}
              <ExpandableSection title="Sales & Marketing" icon={Megaphone}>
                <div className="space-y-2">
                  {Object.entries(year.expenses.marketing).map(([key, value]) => {
                    if (key === 'total') return null;
                    const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
                    return (
                      <div key={key} className="flex justify-between items-center py-1">
                        <span className="text-slate-600 text-sm">{label}</span>
                        <span className="text-slate-700">{formatCurrency(value)}</span>
                      </div>
                    );
                  })}
                  <div className="flex justify-between items-center py-2 bg-slate-100 rounded px-2 -mx-2 mt-2">
                    <span className="text-slate-900 font-semibold">Total Marketing</span>
                    <span className="text-slate-700 font-semibold">{formatCurrency(year.expenses.marketing.total)}</span>
                  </div>
                </div>
              </ExpandableSection>

              {/* Software Costs */}
              <ExpandableSection title="Software & Tools" icon={Briefcase}>
                <div className="space-y-2">
                  {Object.entries(year.expenses.software).map(([key, value]) => {
                    if (key === 'total') return null;
                    const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
                    return (
                      <div key={key} className="flex justify-between items-center py-1">
                        <span className="text-slate-600 text-sm">{label}</span>
                        <span className="text-slate-700">{formatCurrency(value)}</span>
                      </div>
                    );
                  })}
                  <div className="flex justify-between items-center py-2 bg-slate-100 rounded px-2 -mx-2 mt-2">
                    <span className="text-slate-900 font-semibold">Total Software</span>
                    <span className="text-slate-700 font-semibold">{formatCurrency(year.expenses.software.total)}</span>
                  </div>
                </div>
              </ExpandableSection>

              {/* Operations Costs */}
              <ExpandableSection title="General & Administrative" icon={Shield}>
                <div className="space-y-2">
                  {Object.entries(year.expenses.operations).map(([key, value]) => {
                    if (key === 'total') return null;
                    const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
                    return (
                      <div key={key} className="flex justify-between items-center py-1">
                        <span className="text-slate-600 text-sm">{label}</span>
                        <span className="text-slate-700">{formatCurrency(value)}</span>
                      </div>
                    );
                  })}
                  <div className="flex justify-between items-center py-2 bg-slate-100 rounded px-2 -mx-2 mt-2">
                    <span className="text-slate-900 font-semibold">Total G&A</span>
                    <span className="text-slate-700 font-semibold">{formatCurrency(year.expenses.operations.total)}</span>
                  </div>
                </div>
              </ExpandableSection>
            </div>

            {/* Year Summary Box */}
            <div className={`rounded-xl p-4 border ${
              year.metrics.netIncome >= 0 
                ? 'bg-slate-100 border-slate-200' 
                : 'bg-slate-100 border-slate-200'
            }`}>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div>
                  <p className="text-slate-600 text-xs">Total Revenue</p>
                  <p className="text-slate-900 font-bold">{formatCurrency(year.revenue.total)}</p>
                </div>
                <div>
                  <p className="text-slate-600 text-xs">Total Expenses</p>
                  <p className="text-slate-900 font-bold">{formatCurrency(year.expenses.totalExpenses)}</p>
                </div>
                <div>
                  <p className="text-slate-600 text-xs">Net Income</p>
                  <p className={`font-bold ${year.metrics.netIncome >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                    {formatCurrency(year.metrics.netIncome)}
                  </p>
                </div>
                <div>
                  <p className="text-slate-600 text-xs">Gross Margin</p>
                  <p className="text-slate-900 font-bold">{formatPercent(year.metrics.grossMargin)}</p>
                </div>
                <div>
                  <p className="text-slate-600 text-xs">LTV:CAC Ratio</p>
                  <p className={`font-bold ${year.metrics.ltvCacRatio >= 3 ? 'text-emerald-700' : 'text-slate-800'}`}>
                    {year.metrics.ltvCacRatio}:1
                  </p>
                </div>
              </div>
            </div>
          </section>
        ))}

        {/* Key Assumptions */}
        <section>
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Calculator className="w-5 h-5 text-blue-900" />
            Key Assumptions (Lean Model)
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white border border-slate-200 rounded-xl p-4 border border-slate-200">
              <h3 className="font-semibold text-slate-900 mb-3">Revenue Assumptions</h3>
              <ul className="space-y-2 text-sm text-slate-700">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-700 flex-shrink-0 mt-0.5" />
                  Blended avg revenue per location: $1,000-1,200/month
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-700 flex-shrink-0 mt-0.5" />
                  Setup fees increase with deal complexity (Y1: $18K → Y3: $55K)
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-700 flex-shrink-0 mt-0.5" />
                  5% annual customer churn rate
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-700 flex-shrink-0 mt-0.5" />
                  10% expansion revenue from existing customers
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-700 flex-shrink-0 mt-0.5" />
                  Customer lifetime: 6+ years (industry average)
                </li>
              </ul>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-4 border border-slate-200">
              <h3 className="font-semibold text-slate-900 mb-3">Lean Operations Model</h3>
              <ul className="space-y-2 text-sm text-slate-700">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-700 flex-shrink-0 mt-0.5" />
                  100% remote workforce - no office overhead
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-700 flex-shrink-0 mt-0.5" />
                  Hire only when triggers met (revenue/ticket volume)
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-700 flex-shrink-0 mt-0.5" />
                  Player/coach model - managers also contribute
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-700 flex-shrink-0 mt-0.5" />
                  Contractors for overflow, not permanent bloat
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-700 flex-shrink-0 mt-0.5" />
                  Founder salary deferred in Year 1
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* Risk Factors */}
        <section>
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-slate-800" />
            Risk Factors & Mitigations
          </h2>
          <div className="bg-white border border-slate-200 rounded-xl p-4 border border-slate-200">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-slate-800 mb-2">Risks</h4>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li>• Longer enterprise sales cycles (6-12 months)</li>
                  <li>• Healthcare regulatory changes</li>
                  <li>• Competition from established CMMS vendors</li>
                  <li>• Hiring challenges for specialized roles</li>
                  <li>• Customer concentration risk in early years</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-emerald-700 mb-2">Mitigations</h4>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li>• Pipeline of 3x target for buffer</li>
                  <li>• HIPAA-first architecture, compliance team</li>
                  <li>• Healthcare-specific features competitors lack</li>
                  <li>• Competitive salaries + remote flexibility</li>
                  <li>• Diversify across regions and system sizes</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Funding Requirements */}
        <section>
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-blue-900" />
            Funding Strategy
          </h2>
          <div className="bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 rounded-xl p-6 border border-slate-200">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-slate-900 mb-2">Bootstrap-First Model</h3>
              <p className="text-slate-700">Profitable from Year 1 — External funding optional, not required</p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center bg-white border border-slate-200 rounded-lg p-4">
                <p className="text-slate-600 text-sm mb-1">Year 1 Profit</p>
                <p className="text-2xl font-bold text-emerald-700">$263K</p>
                <p className="text-xs text-slate-600 mt-1">35% margin, self-funded</p>
              </div>
              <div className="text-center bg-white border border-slate-200 rounded-lg p-4">
                <p className="text-slate-600 text-sm mb-1">Year 2 Profit</p>
                <p className="text-2xl font-bold text-emerald-700">$1.21M</p>
                <p className="text-xs text-slate-600 mt-1">42% margin, funds Y3 growth</p>
              </div>
              <div className="text-center bg-white border border-slate-200 rounded-lg p-4">
                <p className="text-slate-600 text-sm mb-1">Year 3 Profit</p>
                <p className="text-2xl font-bold text-emerald-700">$4.08M</p>
                <p className="text-xs text-slate-600 mt-1">51% margin, ready for exit</p>
              </div>
            </div>
            <div className="mt-6 pt-4 border-t border-slate-200">
              <p className="text-slate-600 text-sm text-center mb-3">Optional: Accelerate growth with external capital</p>
              <div className="grid md:grid-cols-2 gap-4 max-w-2xl mx-auto">
                <div className="bg-white/30 rounded-lg p-3 text-center">
                  <p className="text-blue-900 font-semibold">Seed Round ($500K-1M)</p>
                  <p className="text-xs text-slate-600">Accelerate hiring, reach Y2 targets in 8 months</p>
                </div>
                <div className="bg-white/30 rounded-lg p-3 text-center">
                  <p className="text-blue-900 font-semibold">Series A ($3-5M)</p>
                  <p className="text-xs text-slate-600">At $2M+ ARR, aggressive national expansion</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="text-center py-8">
          <p className="text-slate-600 mb-4">Interested in learning more about investment opportunities?</p>
          <div className="flex justify-center gap-4">
            <Link to="/investor">
              <Button className="bg-cyan-600 hover:bg-cyan-700">
                Back to Investor Portal
              </Button>
            </Link>
            <a href="mailto:george@sterilemate.com?subject=Investment Inquiry">
              <Button variant="outline" className="border-slate-600 text-slate-700 hover:bg-white">
                Contact Founder
              </Button>
            </a>
          </div>
        </section>
      </main>
    </div>
  );
}
